import { useCallback } from 'react';

import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import { useSelectedVersion } from '@client/store/ui';
import {
  useGetProposalColor,
  useProposalsResultRecords,
} from '@features/proposals/use-proposals.hooks';
import { useProjectBuildingGFA } from '@client/store/project';
import { useEffectiveSbefGroups } from '@features/sbef/use-effective-sbef-groups.hook';
import { useSelectedLifecycles } from '@features/lifecycles/LifecyclesSelect';
import { useConservativeGenericCarbon } from '@features/results/conservative-generic-carbon.hook';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import type { ResultsRecord } from '@shared/models/unit.model';

import type { ProductGroupsChartData } from './report-product-groups.model';
import {
  buildReportProductGroupsChartData,
  versionRollupCo2ePerGfa,
} from './report-product-groups-data.helpers';
import { filterProductGroupChartResultsRecord } from './report-product-groups-results.helpers';

/**
 * Returns a stable callback that produces the {@link ProductGroupsChartData}
 * for a given proposal by comparing its product-group emissions against the
 * baseline version.
 *
 * Used by the report's tender-proposal slide (baseline + each real proposal).
 * Returns `undefined` when the scenario has no measurable product-group
 * emissions.
 *
 * Slice weights and cloud values are kg CO₂e per m² from product categories.
 * The detail KPI uses the same version rollup as Build and the summary slide.
 */
export const useGetReportProductGroupsDataByProposal = (): ((
  proposalId: IProposal['id'],
) => ProductGroupsChartData | undefined) => {
  const version = useSelectedVersion();
  const rawProposalRecords = useProposalsResultRecords(false);
  const rollupRecords = useProposalsResultRecords();
  const gfa = useProjectBuildingGFA();
  const getProposalColor = useGetProposalColor();
  const effectiveSbefGroups = useEffectiveSbefGroups();
  const selectedLifecycles = useSelectedLifecycles();
  const conservative = useConservativeGenericCarbon();

  const filterResultsRecord = useCallback(
    (record: ResultsRecord) =>
      filterProductGroupChartResultsRecord(record, {
        lifecycles: selectedLifecycles,
        conservative,
      }),
    [selectedLifecycles, conservative],
  );

  return useCallback(
    (proposalId) => {
      if (!version) return undefined;

      const isBaselineScenario = proposalId === version.id;
      const scenarioVersion = isBaselineScenario
        ? version
        : getResolvedBuildingVersion(version, proposalId);

      return buildReportProductGroupsChartData({
        baselineVersion: version,
        scenarioVersion,
        baselineRecord: filterResultsRecord(
          rawProposalRecords[version.id] ?? {},
        ),
        scenarioRecord: filterResultsRecord(
          rawProposalRecords[proposalId] ?? {},
        ),
        selectedSbefGroups: effectiveSbefGroups,
        gfa,
        isBaselineScenario,
        accentColor: isBaselineScenario
          ? BASELINE_COLOR
          : getProposalColor(proposalId),
        baselineRollupCo2ePerGfa: versionRollupCo2ePerGfa(
          rollupRecords[version.id],
          version.id,
          gfa,
        ),
        scenarioRollupCo2ePerGfa: versionRollupCo2ePerGfa(
          rollupRecords[proposalId],
          version.id,
          gfa,
        ),
      });
    },
    [
      version,
      rawProposalRecords,
      filterResultsRecord,
      effectiveSbefGroups,
      gfa,
      getProposalColor,
      rollupRecords,
    ],
  );
};
